import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { KanbanSquare, GripVertical, Share2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/confetti";
import { PIPELINE_COLUMNS, TYPE_LABEL } from "@/lib/constants";
import { FlagBadges } from "@/components/Badges";
import { ContentDrawer } from "@/components/ContentDrawer";
import { ContentCreateDialog } from "@/components/ContentCreateDialog";
import { CurateDialog } from "@/components/CurateDialog";
import { ShareToChatDialog } from "@/components/ShareToChatDialog";
import { thumbUrl } from "@/components/FileUpload";
import { useAccount } from "@/context/AccountContext";

export default function Pipeline() {
  const { accountKey, search, refreshApprovals } = useOutletContext();
  const { current } = useAccount();
  const [items, setItems] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [selected, setSelected] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get("/content", { params: { account: accountKey } }).then(({ data }) => {
    setItems(data);
    // Ein geoeffneter Drawer muss Statusaenderungen des Schedulers ebenfalls
    // erhalten; sonst bleibt nach erfolgreichem Publish der alte Fehler stehen.
    setSelected((currentItem) => currentItem
      ? data.find((entry) => entry.id === currentItem.id) || currentItem
      : null);
  });

  useEffect(() => {
    if (!accountKey) return undefined;
    load();
    const refreshTimer = window.setInterval(load, 15000);
    return () => window.clearInterval(refreshTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus) return;
    api
      .get(`/content/${focus}`)
      .then(({ data }) => setSelected(data))
      .catch(() => toast.error("Beitrag nicht gefunden"))
      .finally(() => setSearchParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return q ? items.filter((i) => i.title.toLowerCase().includes(q) || (i.hook || "").toLowerCase().includes(q)) : items;
  }, [items, search]);

  const onDrop = async (col) => {
    setOverCol(null);
    const item = items.find((i) => i.id === dragId);
    setDragId(null);
    if (!item || col.statuses.includes(item.status)) return;
    const status = col.drop;
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status } : i)));
    try {
      await api.patch(`/content/${item.id}`, { status });
      if (status === "approved") celebrate();
      toast.success(`„${item.title}" → ${col.label}`);
      if (status === "approved" && item.scheduled_date && !item.media_url && !item.video_url)
        toast.warning(`⚠️ „${item.title}": Kein Bild/Video angehängt — die geplante Instagram-Veröffentlichung wird fehlschlagen`, { duration: 8000 });
      refreshApprovals?.();
    } catch {
      toast.error("Verschieben fehlgeschlagen");
      load();
    }
  };

  return (
    <div className="p-6 md:p-8 animate-rise">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
              <KanbanSquare className="h-3.5 w-3.5" /> Content-Pipeline
              {current && (
                <span
                  data-testid="pipeline-channel-badge"
                  className="normal-case tracking-normal inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-2.5 py-0.5 ml-1"
                  style={{ background: `${current.accent}14`, color: current.accent, border: `1px solid ${current.accent}33` }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: current.accent }} />
                  {current.name}
                </span>
              )}
            </p>
            <h1 className="font-head text-4xl font-bold text-neutral-900 tracking-tighter mt-1">Von der Idee bis zur Veröffentlichung</h1>
            <p className="text-sm text-neutral-400 mt-1">Karten per Drag &amp; Drop verschieben — oder rechts neuen Inhalt anlegen &amp; Video hochladen.</p>
          </div>
          <div className="flex items-center gap-2">
            <CurateDialog accountKey={accountKey} onCreated={() => { load(); refreshApprovals?.(); }} />
            <ContentCreateDialog accountKey={accountKey} onCreated={() => { load(); refreshApprovals?.(); }} />
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto kanban-scroll pb-4">
        <div className="flex gap-4 min-w-max px-0.5">
          {PIPELINE_COLUMNS.map((col) => {
            const colItems = filtered.filter((i) => col.statuses.includes(i.status));
            return (
              <div
                key={col.key}
                data-testid={`kanban-col-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
                onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                onDrop={() => onDrop(col)}
                className={`w-72 shrink-0 rounded-2xl border transition-colors ${
                  overCol === col.key ? "border-[#007AFF]/50 bg-[#007AFF]/5" : "border-transparent bg-zinc-200/50"
                }`}
              >
                <div className="flex items-center justify-between px-3.5 py-3 border-b border-neutral-200/70">
                  <span className="font-head font-semibold text-sm text-neutral-800">{col.label}</span>
                  <span className="text-xs font-semibold text-neutral-400 bg-white border border-neutral-200 rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">
                    {colItems.length}
                  </span>
                </div>
                <div className="p-2.5 space-y-2.5 min-h-[120px]">
                  {colItems.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`kanban-card-${item.id}`}
                      draggable
                      onDragStart={() => setDragId(item.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => setSelected(item)}
                      className={`group bg-white rounded-xl p-3 cursor-pointer shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-md active:scale-[0.98] transition-all ${
                        dragId === item.id ? "opacity-40" : ""
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {item.media_url && <img src={thumbUrl(item.media_url, 160)} alt="" className="h-12 w-10 rounded object-cover shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-400">{TYPE_LABEL[item.content_type]}</span>
                            <button
                              data-testid={`share-card-${item.id}`}
                              title="Im Chat teilen"
                              onClick={(e) => { e.stopPropagation(); setShareItem(item); }}
                              className="ml-auto h-6 w-6 rounded flex items-center justify-center text-neutral-300 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Share2 className="h-3.5 w-3.5" />
                            </button>
                            <GripVertical className="h-3.5 w-3.5 text-neutral-300 opacity-0 group-hover:opacity-100" />
                          </div>
                          <h4 className="text-[13px] font-medium text-neutral-900 leading-snug mt-0.5 line-clamp-2">{item.title}</h4>
                          {item.status === "review" && (
                            <span className="mt-1 inline-block text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Wartet auf Freigabe</span>
                          )}
                          {item.status === "scheduled" && (
                            <span className="mt-1 inline-block text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">Geplant</span>
                          )}
                          {item.status === "schedule_failed" && (
                            <span className="mt-1 inline-block text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">Planung fehlgeschlagen</span>
                          )}
                        </div>
                      </div>
                      {item.flags?.length > 0 && <div className="mt-2"><FlagBadges flags={item.flags} /></div>}
                      {item.scheduled_date && (
                        <div className="mt-2" data-testid={`card-date-${item.id}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">
                            <CalendarDays className="h-3 w-3" />
                            {(() => {
                              const d = new Date(item.scheduled_date);
                              if (Number.isNaN(d.getTime())) return item.scheduled_date;
                              const date = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
                              const hasTime = String(item.scheduled_date).includes("T") && !String(item.scheduled_date).endsWith("T00:00");
                              return hasTime ? `${date} · ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr` : date;
                            })()}
                          </span>
                        </div>
                      )}
                      {item.assigned_creator && (
                        <div className="mt-2 text-[11px] text-neutral-400">{item.assigned_creator}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ContentDrawer item={selected} onClose={() => setSelected(null)} onChanged={() => { load(); refreshApprovals?.(); }} />
      <ShareToChatDialog item={shareItem} onClose={() => setShareItem(null)} />
    </div>
  );
}
